import { useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import _concat from 'lodash/concat';
import _isEmpty from 'lodash/isEmpty';
import service from '../../../apis/service';
import { apiEndpoints } from '../../../constants/apiEndpoints';
import { showProgress } from '../../../redux/actions/DealershipActions';
import { ErrorToast } from '../../utils/Utils';
import { normalizeDealershipListResult, getDealershipIdsWithSelectedFlag } from '../dealershipListHelpers';

const PAGE_SIZE = 100;

const useDealershipFetch = (planId, filterObjectInitial, { onPreselectedIds, onReset } = {}) => {
  const dispatch = useDispatch();

  const [dealerships, setDealerships] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilters, setActiveFilters] = useState({});
  const [activeSearch, setActiveSearch] = useState('');

  // Refs so callbacks never need to be in fetchDealerships' dep array
  const onPreselectedIdsRef = useRef(onPreselectedIds);
  const onResetRef = useRef(onReset);
  useEffect(() => { onPreselectedIdsRef.current = onPreselectedIds; }, [onPreselectedIds]);
  useEffect(() => { onResetRef.current = onReset; }, [onReset]);

  const fetchDealerships = useCallback(
    async (filtersPayload, page = 1, search = '') => {
      if (!planId || _isEmpty(filtersPayload)) return;

      const url = apiEndpoints.PLAN_DEALERS_BY_ID.replace('ID', planId);

      dispatch(showProgress(true));
      try {
        const result = await service.post(url, {
          filters: filtersPayload,
          page,
          size: PAGE_SIZE,
          search,
        });
        const list = normalizeDealershipListResult(result);

        if (page === 1) {
          setDealerships(list);
          if (!search) {
            setTotalCount(result?.totalCount ?? list.length);
          }
          onResetRef.current?.();
        } else {
          setDealerships((prev) => _concat(prev, list));
        }

        setHasMore(page < (result?.totalPages ?? 1));
        setCurrentPage(page);
        setActiveFilters(filtersPayload);
        setActiveSearch(search);

        const selectedIds = getDealershipIdsWithSelectedFlag(list);
        if (selectedIds.length > 0) {
          onPreselectedIdsRef.current?.(selectedIds);
        }
      } catch (error) {
        ErrorToast(error);
      }
      dispatch(showProgress(false));
    },
    [planId, dispatch],
  );

  useEffect(() => {
    if (!planId || _isEmpty(filterObjectInitial)) return;
    fetchDealerships(filterObjectInitial, 1, '');
  }, [planId, filterObjectInitial, fetchDealerships]);

  const fetchNextPage = useCallback(() => {
    if (!hasMore) return;
    fetchDealerships(activeFilters, currentPage + 1, activeSearch);
  }, [hasMore, activeFilters, currentPage, activeSearch, fetchDealerships]);

  // Called when the user clicks Apply in the filter bar
  const applyFilters = useCallback(
    (filtersPayload) => {
      fetchDealerships(filtersPayload, 1, activeSearch);
    },
    [activeSearch, fetchDealerships],
  );

  // Called when the search box value changes
  const onSearch = useCallback(
    (searchValue) => {
      fetchDealerships(activeFilters, 1, searchValue);
    },
    [activeFilters, fetchDealerships],
  );

  return {
    dealerships,
    totalCount,
    hasMore,
    fetchNextPage,
    applyFilters,
    onSearch,
    activeFilters,
  };
};

export default useDealershipFetch;
